import { useState, useEffect, Fragment } from 'react';
import { useSelector } from 'react-redux';
import { selectAccessToken } from '../../../slices/staffSlice';
import { Topbar, Sidebar, Loader, FormInput, Button } from "../../../components";
import { fetchRequests } from "../../../Requests/getRequests";
import { authStaff, staffRoles, generateSlug } from '../../../utils/constants';
import { Dialog, Transition } from '@headlessui/react';
import { HugeiconsIcon } from '@hugeicons/react';
import { UserIcon, FileAddIcon, Cancel01Icon } from '@hugeicons/core-free-icons';
import { show } from '../../../slices/toastSlice';
import { useDispatch } from 'react-redux';

function Index() {

    const accessToken = useSelector(selectAccessToken);
    const dispatch = useDispatch();
    const [roles, setRoles] = useState([]);
    const [allPermissions, setAllPermissions] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [name, setName] = useState("");
    const [selectedPerms, setSelectedPerms] = useState([]);
    const [loader, setLoader] = useState(true);
    const [process, setProcess] = useState(false);

    const closeModal = () => {
        setIsOpen(false);
        setName("");
        setSelectedPerms([]);
    };

    const openModal = () => setIsOpen(true);

    const togglePerm = (slug) => {
        setSelectedPerms(prev =>
            prev.includes(slug) ? prev.filter(s => s !== slug) : [...prev, slug]
        );
    };

    const createRequest = async (e) => {
        e.preventDefault();
        if (!name) return;
        setProcess(true);

        const headers = authStaff(accessToken);
        const res = await fetchRequests(`/auth/roles/`, "POST", headers, {}, { name, permissions: selectedPerms });
        if (res?.err || res?.code) {
            dispatch(show({ type: "error", state: true, message: res?.err || res?.message }));
        } else {
            const updated = await fetchRequests(`/auth/roles/`, "GET", headers, {}, {});
            if (!updated?.err) setRoles(updated?.roles || []);
            dispatch(show({ type: "success", state: true, message: "Role created successfully" }));
            closeModal();
        }
        setProcess(false);
    };

    useEffect(() => {
        let unsubscribed = false;
        const load = async () => {
            const headers = authStaff(accessToken);
            const [rolesRes, permsRes] = await Promise.all([
                fetchRequests(`/auth/roles/`, "GET", headers, {}, {}),
                fetchRequests(`/auth/roles/permissions`, "GET", headers, {}, {})
            ]);
            if (!unsubscribed) {
                setLoader(false);
                if (!rolesRes?.err) setRoles(rolesRes?.roles || []);
                if (!permsRes?.err) setAllPermissions(permsRes?.permissions || []);
            }
        };
        load();
        return () => { unsubscribed = true; };
    }, [accessToken]);

    return (
        <>
        <div className="main-container">
            <Topbar />
            <Sidebar />

            <main className="main-content">
                <section className="section-container space-y-6">

                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center shrink-0">
                                <HugeiconsIcon icon={UserIcon} size={18} className="text-indigo-600" />
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-50">Roles</h1>
                                    {roles.length > 0 && (
                                        <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-full">
                                            {roles.length}
                                        </span>
                                    )}
                                    {loader && <Loader color="text-gray-400" size="w-4 h-4" />}
                                </div>
                                <p className="text-sm text-gray-400">Staff roles and their assigned permissions</p>
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={openModal}
                            className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-[#1e3a6e] rounded-lg hover:bg-[#264d91] transition-colors shrink-0"
                        >
                            <HugeiconsIcon icon={FileAddIcon} size={15} />
                            Add Role
                        </button>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
                        <table className="min-w-full">
                            <thead>
                                <tr className="bg-gray-50/60 dark:bg-gray-900/60 border-b border-gray-100 dark:border-gray-700">
                                    <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide w-[35%]">Name</th>
                                    <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide w-[35%]">Slug</th>
                                    <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Permissions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                                {roles.length > 0 ? (
                                    roles.map((role, index) => (
                                        <tr key={index} className="hover:bg-gray-50/40 dark:hover:bg-gray-700/30 transition-colors">
                                            <td className="px-5 py-3.5 text-sm font-medium text-gray-800 dark:text-gray-100">
                                                {staffRoles(role?.slug)}
                                            </td>
                                            <td className="px-5 py-3.5 text-sm text-gray-500 dark:text-gray-400 font-mono">
                                                {role?.slug}
                                            </td>
                                            <td className="px-5 py-3.5">
                                                <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-500/20 rounded-full">
                                                    {role?.permissions?.length ?? 0} permissions
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="3" className="px-5 py-12 text-center text-sm text-gray-400">
                                            {loader ? 'Loading...' : 'No roles found'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                </section>
            </main>
        </div>

        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={closeModal}>
                <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
                    <div className="fixed inset-0 bg-black/30" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                            <Dialog.Panel className="w-full max-w-lg bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden">
                                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700">
                                    <Dialog.Title className="text-base font-semibold text-gray-900 dark:text-gray-50">
                                        Add a New Role
                                    </Dialog.Title>
                                    <button type="button" onClick={closeModal} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
                                        <HugeiconsIcon icon={Cancel01Icon} size={16} />
                                    </button>
                                </div>

                                <form onSubmit={createRequest} className="px-6 py-5 space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <FormInput
                                                label="Name"
                                                type="text"
                                                name="name"
                                                id="name"
                                                value={name}
                                                setValue={setName}
                                            />
                                        </div>
                                        <div>
                                            <FormInput
                                                label="Slug"
                                                disabled
                                                value={generateSlug(name)}
                                            />
                                        </div>
                                    </div>

                                    {allPermissions.length > 0 && (
                                        <div>
                                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Permissions
                                                <span className="ml-1.5 text-xs font-normal text-gray-400">({selectedPerms.length} selected)</span>
                                            </p>
                                            <div className="grid grid-cols-2 gap-1.5 max-h-52 overflow-y-auto border border-gray-100 dark:border-gray-700 rounded-lg p-3 bg-gray-50/50 dark:bg-gray-900/40">
                                                {allPermissions.map((perm, i) => (
                                                    <label key={i} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer hover:text-gray-900 dark:hover:text-gray-100 py-0.5">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedPerms.includes(perm.slug)}
                                                            onChange={() => togglePerm(perm.slug)}
                                                            className="rounded border-gray-300 dark:border-gray-600 text-[#1e3a6e]"
                                                        />
                                                        {perm.name || perm.slug}
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex items-center gap-3 pt-1">
                                        <Button
                                            btnText="Create Role"
                                            btnClasses="inline-flex items-center px-4 py-2.5 text-sm font-medium text-white bg-[#1e3a6e] rounded-lg hover:bg-[#264d91] transition-colors"
                                            btnFunction={createRequest}
                                            btnType="submit"
                                            processing={process}
                                        />
                                        <button type="button" onClick={closeModal} className="px-4 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors">
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
        </>
    );
}

export default Index;
